import { takeLatest, call, put, all, take, fork, cancel, cancelled } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import { delay } from 'redux-saga'
import { push as pushHistory } from 'react-router-redux'

import actions, { constants } from 'common/actions/system'
import snackbarActions from 'common/actions/snackbar'

import * as schemas from 'schemas'
import * as api from 'api/system'
import * as Strings from 'constants/Strings'
import * as Routes from 'constants/Routes'

const POLLING_DELAY = 600000 // 10 minute
let pollSystemsTask = null

export function* onLoadSystems({ payload: data }) {
  yield put(actions.loadSystemsRequest.start())

  try {
    let { systems } = yield call(api.loadSystems, data)
    const norm = yield call(normalize, systems, [schemas.system])
    yield put(actions.loadSystemsRequest.success(norm))
    return systems
  } catch (err) {
    yield put(actions.loadSystemsRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
    yield put(pushHistory(Routes.LOGIN))
  }
}

export function* onPollSystems() {
  try {
    while (true) {
      yield put(actions.pollSystemsRequest.start())
      try {
        const { systems } = yield call(api.loadSystems)
        const norm = yield call(normalize, systems, [schemas.system])

        yield put(actions.pollSystemsRequest.success(norm))
        yield delay(POLLING_DELAY)
      } catch (err) {
        // Stop polling task when error
        yield put(actions.pollSystemsRequest.failure(err))
        yield put(snackbarActions.showSnackbar('snackbar.whoops'))
        yield put(actions.pollSystemsStop())
      }
    }
  } finally {
    if (yield cancelled()) {
    }
  }
}

function* watchPollSystems() {
  while (true) {
    yield take(constants.POLL_SYSTEMS_START)

    // Start polling task
    pollSystemsTask = yield fork(onPollSystems)

    // Wait until POLL_SYSTEM_STOP action
    yield take(constants.POLL_SYSTEMS_STOP)

    // Cancel polling task
    yield cancel(pollSystemsTask)
  }
}

export function* onLoadLastVersions() {
  yield put(actions.loadLastVersionsRequest.start())

  try {
    const { lastVersions } = yield call(api.loadLastVersions)
    const norm = yield call(normalize, lastVersions, schemas.lastVersions)

    yield put(actions.loadLastVersionsRequest.success(norm))
  } catch (err) {
    yield put(actions.loadLastVersionsRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
    yield put(pushHistory(Routes.LOGIN))
  }
}

export function* onLoadSystem({ payload: deviceId }) {
  yield put(actions.loadSystemRequest.start())

  try {
    const { system } = yield call(api.loadSystem, deviceId)
    const norm = yield call(normalize, system, schemas.device)

    yield put(actions.loadSystemRequest.success(norm))
  } catch (err) {
    yield put(actions.loadSystemRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  }
}

export function* onUpdateResetSystem({ payload: data, meta }) {
  yield put(actions.updateResetSystemRequest.start())
  try {
    yield call(api.updateResetSystem, data.data, data.deviceId)

    yield put(actions.updateResetSystemRequest.success({ data }))
    yield put(snackbarActions.showSnackbar(Strings.SUCCESSFUL_UPDATE_RESET))
  } catch (err) {
    yield put(actions.updateResetSystemRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  } finally {
    if (meta && meta.onComplete) {
      yield call(meta.onComplete)
    }
  }
}

export function* onUpdateDeviceName({ payload: data, meta }) {
  yield put(actions.updateDeviceNameRequest.start())
  let isSuccess = false

  try {
    const response = yield call(api.changeDeviceName, data.data, data.deviceId)

    if (!response) {
      throw new Error('Invalid new_name value')
    }

    if (!response.device) {
      throw new Error('Error, no se ha actualizado correctamente')
    }

    yield put(
      actions.updateDeviceNameRequest.success({
        data: {
          device: response.device,
          deviceId: data.deviceId,
          newName: data.data.new_name
        }
      })
    )
    yield put(snackbarActions.showSnackbar(Strings.SUCCESSFUL_UPDATE_DEVICE_NAME))
    isSuccess = true
  } catch (err) {
    yield put(actions.updateDeviceNameRequest.failure(err))
    yield put(snackbarActions.showSnackbar(err.message || Strings.WHOOPS))
  } finally {
    if (meta && meta.onComplete) {
      yield call(meta.onComplete, isSuccess)
    }
  }
}

export function* onDeleteSystem({ payload: id }) {
  yield put(actions.deleteSystemRequest.start())

  try {
    yield call(api.deleteSystem, id)

    yield put(actions.deleteSystemRequest.success({ id }))
    yield put(snackbarActions.showSnackbar('Dispositivo eliminado con éxito'))
  } catch (err) {
    yield put(actions.deleteSystemRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  }
}

export function* onDeleteSystems({ payload: data }) {
  yield put(actions.deleteSystemsRequest.start())

  try {
    yield put(actions.deleteSystemsRequest.success({ data }))
  } catch (err) {
    yield put(actions.deleteSystemsRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  }
}

export function* onDeleteSystemData({ payload: id }) {
  yield put(actions.deleteSystemDataRequest.start())

  try {
    yield call(api.deleteSystemData, id)
    yield put(actions.deleteSystemDataRequest.success())
    yield put(snackbarActions.showSnackbar('Datos de dispositivo eliminados con éxito'))
  } catch (err) {
    yield put(actions.deleteSystemDataRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  }
}

export function* onDeleteSystemFromUser({ payload: data }) {
  yield put(actions.deleteSystemFromUserRequest.start())

  try {
    yield call(api.deleteSystemFromUser, data.id, data.CustomerId)

    yield put(actions.deleteSystemFromUserRequest.success({ data }))
    yield put(snackbarActions.showSnackbar('Dispositivo desasociado de cuenta con éxito'))
  } catch (err) {
    yield put(actions.deleteSystemFromUserRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  }
}

export function* onCreateApiKey({ payload: data, meta }) {
  yield put(actions.createApiKeyRequest.start())

  try {
    const response = yield call(api.createApiKey, data)

    yield put(actions.createApiKeyRequest.success(response))
    yield put(snackbarActions.showSnackbar('API Key creada con éxito'))
  } catch (err) {
    yield put(actions.createApiKeyRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  } finally {
    if (meta && meta.onComplete) {
      yield call(meta.onComplete)
    }
  }
}

export function* onDeleteApiKey({ payload: deviceId, meta }) {
  yield put(actions.deleteApiKeyRequest.start())

  try {
    const response = yield call(api.deleteApiKey, deviceId)

    yield put(
      actions.deleteApiKeyRequest.success({
        ...(response || {}),
        deviceId
      })
    )
    yield put(snackbarActions.showSnackbar('API Key eliminada con éxito'))
  } catch (err) {
    yield put(actions.deleteApiKeyRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  } finally {
    if (meta && meta.onComplete) {
      yield call(meta.onComplete)
    }
  }
}

export function* onSetApiKeyStatus({ payload: data, meta }) {
  yield put(actions.setApiKeyStatusRequest.start())

  try {
    const response = yield call(api.setApiKeyStatus, data)

    yield put(
      actions.setApiKeyStatusRequest.success({
        ...(response || {}),
        deviceId: data.systemId,
        apiKeyDisabled:
          response && response.apiKeyDisabled !== undefined
            ? response.apiKeyDisabled
            : data.newStatus
      })
    )
    yield put(snackbarActions.showSnackbar('Estado de API Key actualizado con éxito'))
  } catch (err) {
    yield put(actions.setApiKeyStatusRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  } finally {
    if (meta && meta.onComplete) {
      yield call(meta.onComplete)
    }
  }
}

export function* onSyncAirtableInformation() {
  yield put(actions.syncAirtableRequest.start())
  try {
    const { info } = yield call(api.syncAirtableInformation)
    yield put(actions.syncAirtableRequest.success())
    yield put(
      snackbarActions.showSnackbar(
        'Se han creado ' + info?.created + ' dispositivos y se han actualizado ' + info?.updated
      )
    )
  } catch (err) {
    yield put(actions.syncAirtableRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
  }
}

export function* onAddDeviceToUser({ payload }) {
  const { customerId, data, onComplete } = payload
  yield put(actions.addDeviceToUserRequest.start())

  try {
    const { body, status } = yield call(api.addDeviceToUser, customerId, data)

    if (status === 411) {
      yield put(actions.addDeviceToUserRequest.failure({ status, body }))
      yield put(snackbarActions.showSnackbar('Límite de dispositivos alcanzado para el usuario'))
      if (onComplete) onComplete({ ok: false, status, body })
      return
    }

    const added = (body && body.system) || []
    const failed = (body && body.systemFailed) || []

    yield put(actions.addDeviceToUserRequest.success({ status, body }))

    if (added.length > 0) {
      yield put(snackbarActions.showSnackbar(`${added.length} dispositivo(s) añadidos con éxito`))
      try {
        const { systems } = yield call(api.loadSystems)
        const norm = yield call(normalize, systems, [schemas.system])
        yield put(actions.loadSystemsRequest.success(norm))
      } catch (e) {
        // Silent: keep user in app even if reload fails
      }
    }
    if (failed.length > 0) {
      yield put(
        snackbarActions.showSnackbar(
          `No se pudieron añadir ${failed.length} dispositivo(s): ${failed
            .map(f => f.id || f)
            .join(', ')}`
        )
      )
    }
    if (onComplete) onComplete({ ok: failed.length === 0, status, body })
  } catch (err) {
    yield put(actions.addDeviceToUserRequest.failure(err))
    yield put(snackbarActions.showSnackbar(Strings.WHOOPS))
    if (onComplete) onComplete({ ok: false, error: err })
  }
}

export default function* watchSystem() {
  yield fork(watchPollSystems)
  yield all([
    takeLatest(constants.LOAD_SYSTEMS, onLoadSystems),
    takeLatest(constants.LOAD_SYSTEM, onLoadSystem),
    takeLatest(constants.LOAD_LAST_VERSIONS, onLoadLastVersions),
    takeLatest(constants.UPDATE_RESET_SYSTEM, onUpdateResetSystem),
    takeLatest(constants.UPDATE_DEVICE_NAME, onUpdateDeviceName),
    takeLatest(constants.DELETE_SYSTEM, onDeleteSystem),
    takeLatest(constants.DELETE_SYSTEMS, onDeleteSystems),
    takeLatest(constants.DELETE_SYSTEM_DATA, onDeleteSystemData),
    takeLatest(constants.DELETE_SYSTEM_FROM_USER, onDeleteSystemFromUser),
    takeLatest(constants.CREATE_API_KEY, onCreateApiKey),
    takeLatest(constants.DELETE_API_KEY, onDeleteApiKey),
    takeLatest(constants.SET_API_KEY_STATUS, onSetApiKeyStatus),
    takeLatest(constants.SYNC_AIRTABLE, onSyncAirtableInformation),
    takeLatest(constants.ADD_DEVICE_TO_USER, onAddDeviceToUser)
  ])
}
